import React from 'react';
import {
  Search,
  Trash2,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  ChevronsDownUp,
  ChevronsUpDown,
} from 'lucide-react';
import { CreateDropdown } from './CreateDropdown';
import { useSceneStore } from '../../store/sceneStore';
import { cn } from '../../utils/cn';

interface HierarchyToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export const HierarchyToolbar: React.FC<HierarchyToolbarProps> = ({
  searchQuery,
  onSearchChange,
}) => {
  const {
    selectedIds,
    gameObjects,
    deleteSelected,
    toggleVisibility,
    toggleLock,
    expandAll,
    collapseAll,
  } = useSceneStore();

  const hasSelection = selectedIds.length > 0;

  // Check if all selected items are locked/hidden
  const allLocked = hasSelection && selectedIds.every((id) => gameObjects[id]?.locked);
  const allHidden = hasSelection && selectedIds.every((id) => !gameObjects[id]?.visible);

  const handleToggleLock = () => {
    selectedIds.forEach((id) => {
      if (gameObjects[id]) {
        // If all are locked, unlock all; otherwise lock all
        if (allLocked) {
          if (gameObjects[id].locked) {
            toggleLock(id);
          }
        } else {
          if (!gameObjects[id].locked) {
            toggleLock(id);
          }
        }
      }
    });
  };

  const handleToggleVisibility = () => {
    selectedIds.forEach((id) => {
      if (gameObjects[id]) {
        // If all are hidden, show all; otherwise hide all
        if (allHidden) {
          if (!gameObjects[id].visible) {
            toggleVisibility(id);
          }
        } else {
          if (gameObjects[id].visible) {
            toggleVisibility(id);
          }
        }
      }
    });
  };

  return (
    <div className="flex flex-col gap-2 p-2 border-b border-zinc-800">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search objects..."
          className="w-full pl-8 pr-3 py-1.5 text-sm bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
          >
            <span className="sr-only">Clear search</span>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1">
        <CreateDropdown />

        <div className="w-px h-4 bg-zinc-700 mx-1" />

        <button
          onClick={deleteSelected}
          disabled={!hasSelection}
          className={cn(
            'p-1.5 rounded transition-colors',
            hasSelection
              ? 'hover:bg-zinc-700 text-zinc-400 hover:text-red-400'
              : 'text-zinc-600 cursor-not-allowed'
          )}
          title="Delete selected (Delete)"
        >
          <Trash2 className="w-4 h-4" />
        </button>

        <button
          onClick={handleToggleLock}
          disabled={!hasSelection}
          className={cn(
            'p-1.5 rounded transition-colors',
            hasSelection
              ? 'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200'
              : 'text-zinc-600 cursor-not-allowed',
            allLocked && hasSelection && 'text-amber-500 hover:text-amber-400'
          )}
          title={allLocked ? 'Unlock selected' : 'Lock selected'}
        >
          {allLocked ? (
            <Lock className="w-4 h-4" />
          ) : (
            <Unlock className="w-4 h-4" />
          )}
        </button>

        <button
          onClick={handleToggleVisibility}
          disabled={!hasSelection}
          className={cn(
            'p-1.5 rounded transition-colors',
            hasSelection
              ? 'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200'
              : 'text-zinc-600 cursor-not-allowed',
            allHidden && hasSelection && 'text-amber-500 hover:text-amber-400'
          )}
          title={allHidden ? 'Show selected' : 'Hide selected'}
        >
          {allHidden ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </button>

        <div className="flex-1" />

        <button
          onClick={expandAll}
          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          title="Expand all"
        >
          <ChevronsUpDown className="w-4 h-4" />
        </button>

        <button
          onClick={collapseAll}
          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          title="Collapse all"
        >
          <ChevronsDownUp className="w-4 h-4" />
        </button>

      </div>
    </div>
  );
};
